import React, { useCallback } from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { PostOverviewModal } from 'components/NewsFeed/PostOverviewModal'
import { CloseButton, BaseModalV3 } from 'components/ui'
import { SkeletonBlock } from 'components/NewTaskDashboard/common'
import { RootState } from 'constants/interfaces'
import { setNotificationPostModal } from 'redux/actions/feed'

const PostModalSkeleton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <Flex alignItems="center" flex="1" minW="0" style={{ gap: '12px' }}>
          <SkeletonBlock h="28px" w="28px" borderRadius="999px" />
          <Box flex="1" minW="0">
            <SkeletonBlock h="13px" w="42%" borderRadius="4px" />
            <SkeletonBlock h="11px" w="26%" borderRadius="4px" mt="8px" />
          </Box>
        </Flex>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Box px="25px" py="8px">
        <SkeletonBlock h="18px" w="74%" borderRadius="4px" mb="12px" />
        <SkeletonBlock h="14px" w="94%" borderRadius="4px" mb="9px" />
        <SkeletonBlock h="14px" w="88%" borderRadius="4px" mb="9px" />
        <SkeletonBlock h="14px" w="66%" borderRadius="4px" mb="22px" />
        <SkeletonBlock h="180px" w="100%" borderRadius="10px" mb="24px" />
        <SkeletonBlock h="13px" w="36%" borderRadius="4px" mb="14px" />
        <Flex alignItems="center" mb="12px" style={{ gap: '12px' }}>
          <SkeletonBlock h="35px" w="35px" borderRadius="999px" />
          <SkeletonBlock h="40px" w="100%" borderRadius="8px" />
        </Flex>
        <Flex alignItems="center" style={{ gap: '12px' }}>
          <SkeletonBlock h="35px" w="35px" borderRadius="999px" />
          <SkeletonBlock h="40px" w="100%" borderRadius="8px" />
        </Flex>
      </Box>
    }
    footer={<SkeletonBlock h="42px" w="100%" borderRadius="10px" />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

const ContentUnavailable: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <BaseModalV3
    isOpen={true}
    onClose={onClose}
    header={
      <Flex alignItems="center" justifyContent="space-between" py="10px">
        <Text fontSize="16px" fontWeight={700} color="#0F172A">
          Notification
        </Text>
        <CloseButton minW="28px" h="28px" p="4px" onClick={onClose} />
      </Flex>
    }
    body={
      <Flex h="100%" minH="320px" alignItems="center" justifyContent="center" flexDirection="column" textAlign="center">
        <Text fontSize="15px" fontWeight={700} color="#0F172A">
          Content is no longer available
        </Text>
        <Text fontSize="13px" color="#64748B" mt="8px" maxW="320px">
          It may have been deleted, moved, or you may no longer have access.
        </Text>
      </Flex>
    }
    footer={<Box />}
    bodyProps={{ style: { background: 'white', height: '70vh' } }}
  />
)

export const NotificationPostModalHost: React.FC = () => {
  const dispatch = useDispatch()
  const postId = useSelector((state: RootState) => state.feed.notificationPostModalID)
  const target = useSelector((state: RootState) => state.feed.notificationPostModalTarget)
  const error = useSelector((state: RootState) => state.feed.notificationPostModalError)
  const post = useSelector((state: RootState) => state.feed.posts.find((item) => item.post_id === postId))

  const handleClose = useCallback(() => {
    dispatch(setNotificationPostModal(null))
  }, [dispatch])

  if (!postId) return null
  if (error) return <ContentUnavailable onClose={handleClose} />
  if (!post) return <PostModalSkeleton onClose={handleClose} />

  return (
    <PostOverviewModal
      isOpen={true}
      onClose={handleClose}
      post={post}
      targetParentCommentId={target?.targetParentCommentId}
      targetCommentId={target?.targetCommentId}
    />
  )
}
